import type { ComponentType } from 'react';
import { Box, Center, Divider, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import type { FigmaIconProps } from '@/assets/icons';
import {
  IconFigmaBrandMark,
  IconFigmaCalendar,
  IconFigmaChevronRight,
  IconFigmaCopy,
  IconFigmaDocument,
  IconFigmaFile,
  IconFigmaFolder,
  IconFigmaGrid,
  IconFigmaGroup,
  IconFigmaHome,
  IconFigmaLayers,
  IconFigmaMail,
  IconFigmaStructure,
  IconFigmaUsers
} from '@/assets/icons';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { formatRole } from '@/utils/format';

interface TopNavItem {
  path: string;
  icon: ComponentType<FigmaIconProps>;
  labelKey: string;
}

const topNavItems: TopNavItem[] = [
  { path: '/', icon: IconFigmaHome, labelKey: 'nav.dashboard' },
  { path: '/board', icon: IconFigmaFolder, labelKey: 'nav.board' },
  { path: '/templates/price-lists', icon: IconFigmaLayers, labelKey: 'nav.templates' },
  { path: '/offers', icon: IconFigmaDocument, labelKey: 'nav.offers' },
  { path: '/contracts', icon: IconFigmaFile, labelKey: 'nav.contracts' },
  { path: '/work-packages', icon: IconFigmaStructure, labelKey: 'nav.workPackages' },
  { path: '/activities', icon: IconFigmaGrid, labelKey: 'nav.activities' },
  { path: '/weekly-reports', icon: IconFigmaCalendar, labelKey: 'nav.weeklyReports' },
  { path: '/invoices', icon: IconFigmaCopy, labelKey: 'nav.invoices' }
];

const adminNavItems: TopNavItem[] = [
  { path: '/admin', icon: IconFigmaHome, labelKey: 'adminNav.dashboard' },
  { path: '/admin/organizations', icon: IconFigmaGrid, labelKey: 'adminNav.organizations' },
  { path: '/admin/users', icon: IconFigmaFile, labelKey: 'adminNav.users' },
  { path: '/admin/invitations', icon: IconFigmaMail, labelKey: 'adminNav.invitations' },
  { path: '/admin/units', icon: IconFigmaStructure, labelKey: 'adminNav.units' }
];

const settingsSubItems = [
  { path: '/users/invite', labelKey: 'nav.settingsInvite' },
  { path: '/users/roles', labelKey: 'nav.settingsUsers' },
  { path: '/users/org-profile', labelKey: 'nav.settingsOrgProfile' },
  { path: '/users/departments', labelKey: 'nav.settingsDepartments' },
  { path: '/users/about', labelKey: 'nav.settingsAbout' }
];

const templatesSubItems = [
  { path: '/templates/price-lists', labelKey: 'userNav.priceLists' },
  { path: '/templates/object-registry', labelKey: 'userNav.objectRegistry' }
];

// Detalj-stranice (npr. /offers/:id, /templates/price-lists/:id) moraju
// zadržati istaknutu stavku/pod-stavku izbornika s koje se na njih došlo —
// obično pravilo za CIJELI izbornik, ne samo "Podloge" (koji je ovo već
// imao preko zasebnog startsWith('/templates')). Neke "index" rute (poput
// '/' za org dio i '/admin' za admin dio) su SAMO iznimka od pravila — one
// nisu prefiks svojih vlastitih detalj-stranica, nego prefiks SVIH ostalih
// stavki u istom izborniku (npr. '/admin/users' počinje s '/admin'), pa bi
// startsWith ovdje uvijek bio istinit i Nadzorna ploča bi ostala označena
// na svakoj stranici te sekcije.
const EXACT_MATCH_ONLY_PATHS = new Set(['/', '/admin']);
const isPathActive = (pathname: string, path: string) =>
  EXACT_MATCH_ONLY_PATHS.has(path)
    ? pathname === path
    : pathname === path || pathname.startsWith(`${path}/`);

const SIDEBAR_TEXT = '#e5ebf1';

const rowStyle = (active: boolean) => ({
  borderRadius: 6,
  color: active ? 'var(--color-text-on-accent)' : SIDEBAR_TEXT,
  backgroundColor: active ? 'var(--color-accent-primary)' : 'transparent',
  fontWeight: active ? 600 : 500,
  cursor: 'pointer' as const
});

const subRowStyle = () => ({
  borderRadius: 0,
  color: SIDEBAR_TEXT,
  cursor: 'pointer' as const
});

const collapsedIconStyle = (active: boolean) => ({
  borderRadius: 8,
  backgroundColor: active ? 'var(--color-accent-primary)' : 'transparent',
  color: active ? 'var(--color-text-on-accent)' : SIDEBAR_TEXT,
  cursor: 'pointer' as const
});

const flyoutRowStyle = (active: boolean) => ({
  borderRadius: 6,
  display: 'flex' as const,
  alignItems: 'center' as const,
  backgroundColor: active ? 'var(--color-accent-primary)' : 'transparent',
  color: active ? 'var(--color-text-on-accent)' : SIDEBAR_TEXT,
  fontWeight: active ? 600 : 400,
  cursor: 'pointer' as const
});

const MINI_LOGO_BG = '#111d34';
const SIDEBAR_WIDTH_EXPANDED = 248;
const SIDEBAR_WIDTH_COLLAPSED = 80;

interface AppNavbarProps {
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const AppNavbar = ({ onNavigate, collapsed = false, onToggleCollapse }: AppNavbarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [hoveredFlyout, setHoveredFlyout] = useState<string | null>(null);

  // Rola, ne URL — app_admin uvijek vidi isti (sužen) izbornik, bez obzira
  // odakle je ušao, i suprotno, org korisnik ga nikad ne vidi ni ako slučajno
  // završi na /admin/* prije nego ga OrgScopedRoute/AppAdminRoute preusmjeri.
  const isAdmin = user?.role === 'APP_ADMIN';
  const isUserRole = !isAdmin && user?.role === 'USER';
  const templatesActive = !isAdmin && location.pathname.startsWith('/templates');
  const partnersActive = !isAdmin && !isUserRole && isPathActive(location.pathname, '/partners');
  const settingsActive = !isAdmin && !isUserRole && location.pathname.startsWith('/users');
  const navItems = isAdmin ? adminNavItems : topNavItems;
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '';

  const goTo = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const filteredNavItems = isUserRole
    ? navItems.filter((item) =>
        ['nav.dashboard', 'nav.board', 'nav.templates'].includes(item.labelKey)
      )
    : navItems;

  const renderFlyout = (
    key: string,
    titleKey: string,
    items: { path: string; labelKey: string }[]
  ) => {
    const isOpen = hoveredFlyout === key;
    return (
      <Box
        style={{
          position: 'absolute',
          left: '100%',
          top: 0,
          minWidth: 220,
          borderRadius: 8,
          backgroundColor: 'var(--color-accent-secondary)',
          border: '1px solid rgba(229,235,241,0.18)',
          boxShadow: '0px 8px 20px rgba(0,0,0,0.35)',
          padding: 8,
          zIndex: 20,
          visibility: isOpen ? 'visible' : 'hidden',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none'
        }}
      >
        <Text
          fz={11}
          fw={600}
          tt="uppercase"
          px={8}
          py={6}
          style={{ color: 'rgba(229,235,241,0.5)', letterSpacing: 0.4 }}
        >
          {t(titleKey)}
        </Text>
        <Stack gap={1}>
          {items.map((sub) => {
            const active = isPathActive(location.pathname, sub.path);
            return (
              <UnstyledButton
                key={sub.path}
                onClick={() => goTo(sub.path)}
                px={8}
                h={34}
                w="100%"
                style={flyoutRowStyle(active)}
              >
                <Text fz={13} style={{ color: 'inherit', fontWeight: 'inherit' }}>
                  {t(sub.labelKey)}
                </Text>
              </UnstyledButton>
            );
          })}
        </Stack>
      </Box>
    );
  };

  const renderNavItems = (isCollapsed: boolean) => (
    <>
      {filteredNavItems.map((item) => {
        const Icon = item.icon;
        const active =
          item.labelKey === 'nav.templates'
            ? templatesActive
            : isPathActive(location.pathname, item.path);

        if (isCollapsed) {
          const hasSubItems = !isAdmin && item.labelKey === 'nav.templates';
          return (
            <div
              key={item.path}
              style={{ position: 'relative' }}
              onMouseEnter={() => hasSubItems && setHoveredFlyout(item.labelKey)}
              onMouseLeave={() => hasSubItems && setHoveredFlyout(null)}
            >
              <UnstyledButton
                onClick={() => goTo(item.path)}
                w="100%"
                h={40}
                pl={12}
                style={{
                  ...collapsedIconStyle(active),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start'
                }}
              >
                <Icon size={20} />
              </UnstyledButton>
              {hasSubItems && renderFlyout(item.labelKey, item.labelKey, templatesSubItems)}
            </div>
          );
        }

        return (
          <div key={item.path}>
            <UnstyledButton
              onClick={() => goTo(item.path)}
              px={12}
              h={40}
              w="100%"
              style={{ ...rowStyle(active), display: 'flex', alignItems: 'center' }}
            >
              <Group gap={14} wrap="nowrap">
                <Icon size={20} />
                <Text fz={13} style={{ color: 'inherit', fontWeight: 'inherit' }}>
                  {t(item.labelKey)}
                </Text>
              </Group>
            </UnstyledButton>

            {item.labelKey === 'nav.templates' && templatesActive && (
              <Stack gap={1} pt={2} pb={6}>
                {templatesSubItems.map((sub) => {
                  const subActive = isPathActive(location.pathname, sub.path);
                  return (
                    <UnstyledButton
                      key={sub.path}
                      onClick={() => goTo(sub.path)}
                      pl={16}
                      pr={12}
                      h={36}
                      w="100%"
                      style={{ ...subRowStyle(), display: 'flex', alignItems: 'center' }}
                    >
                      <Group gap={16} wrap="nowrap" style={{ width: '100%' }}>
                        <Box
                          w={3}
                          h={20}
                          style={{
                            flexShrink: 0,
                            borderRadius: 2,
                            backgroundColor: subActive
                              ? 'var(--color-accent-primary)'
                              : 'transparent'
                          }}
                        />
                        <Text
                          fz={13}
                          lh={1}
                          fw={subActive ? 600 : 400}
                          style={{ color: 'inherit' }}
                        >
                          {t(sub.labelKey)}
                        </Text>
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </Stack>
            )}
          </div>
        );
      })}

      {!isAdmin &&
        !isUserRole &&
        (isCollapsed ? (
          <UnstyledButton
            onClick={() => goTo('/partners')}
            w="100%"
            h={40}
            pl={12}
            style={{
              ...collapsedIconStyle(partnersActive),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <IconFigmaGroup size={20} />
          </UnstyledButton>
        ) : (
          <UnstyledButton
            onClick={() => goTo('/partners')}
            px={12}
            h={40}
            w="100%"
            style={{ ...rowStyle(partnersActive), display: 'flex', alignItems: 'center' }}
          >
            <Group gap={14} wrap="nowrap">
              <IconFigmaGroup size={20} />
              <Text fz={13} style={{ color: 'inherit', fontWeight: 'inherit' }}>
                {t('nav.partners')}
              </Text>
            </Group>
          </UnstyledButton>
        ))}

      {!isAdmin &&
        !isUserRole &&
        (isCollapsed ? (
          <div
            style={{ position: 'relative' }}
            onMouseEnter={() => setHoveredFlyout('nav.settings')}
            onMouseLeave={() => setHoveredFlyout(null)}
          >
            <UnstyledButton
              onClick={() => goTo('/users/invite')}
              w="100%"
              h={40}
              pl={12}
              style={{
                ...collapsedIconStyle(settingsActive),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start'
              }}
            >
              <IconFigmaUsers size={20} />
            </UnstyledButton>
            {renderFlyout('nav.settings', 'nav.settings', settingsSubItems)}
          </div>
        ) : (
          <UnstyledButton
            onClick={() => goTo('/users/invite')}
            px={12}
            h={40}
            w="100%"
            style={{ ...rowStyle(settingsActive), display: 'flex', alignItems: 'center' }}
          >
            <Group gap={14} wrap="nowrap">
              <IconFigmaUsers size={20} />
              <Text fz={13} style={{ color: 'inherit', fontWeight: 'inherit' }}>
                {t('nav.settings')}
              </Text>
            </Group>
          </UnstyledButton>
        ))}

      {settingsActive && !isCollapsed && (
        <Stack gap={1} pt={2} pb={6}>
          {settingsSubItems.map((sub) => {
            const active = isPathActive(location.pathname, sub.path);
            return (
              <UnstyledButton
                key={sub.path}
                onClick={() => goTo(sub.path)}
                pl={16}
                pr={12}
                h={36}
                w="100%"
                style={{ ...subRowStyle(), display: 'flex', alignItems: 'center' }}
              >
                <Group gap={16} wrap="nowrap" style={{ width: '100%' }}>
                  <Box
                    w={3}
                    h={20}
                    style={{
                      flexShrink: 0,
                      borderRadius: 2,
                      backgroundColor: active ? 'var(--color-accent-primary)' : 'transparent'
                    }}
                  />
                  <Text fz={13} lh={1} fw={active ? 600 : 400} style={{ color: 'inherit' }}>
                    {t(sub.labelKey)}
                  </Text>
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      )}
    </>
  );

  const renderFooter = (isCollapsed: boolean) =>
    isCollapsed ? (
      <Stack gap={14} align="center" pt={16} pb={4}>
        <Center
          style={{
            borderRadius: 999,
            backgroundColor: 'var(--color-accent-primary-active)',
            color: 'var(--color-text-on-accent)'
          }}
          w={32}
          h={32}
        >
          <Text fz={12} fw={600}>
            {initials}
          </Text>
        </Center>
        <UnstyledButton
          onClick={logout}
          style={{ color: 'rgba(229,235,241,0.5)', cursor: 'pointer' }}
        >
          <IconLogout size={18} />
        </UnstyledButton>
      </Stack>
    ) : (
      <Group justify="space-between" px={16} py={16}>
        <Group gap={10} wrap="nowrap">
          <Center
            style={{
              borderRadius: 999,
              backgroundColor: 'var(--color-accent-primary-active)',
              color: 'var(--color-text-on-accent)'
            }}
            w={32}
            h={32}
          >
            <Text fz={12} fw={600}>
              {initials}
            </Text>
          </Center>
          <Stack gap={1}>
            <Text fz={13} fw={600} c="white">
              {user ? `${user.firstName} ${user.lastName}` : ''}
            </Text>
            <Text fz={11} c="rgba(229,235,241,0.65)">
              {user ? (user.role === 'USER' ? t('nav.roleUser') : formatRole(user.role)) : ''}
            </Text>
          </Stack>
        </Group>
        <UnstyledButton
          onClick={logout}
          style={{ color: 'rgba(229,235,241,0.5)', cursor: 'pointer' }}
        >
          <IconLogout size={18} />
        </UnstyledButton>
      </Group>
    );

  return (
    <Stack
      h="100%"
      gap={0}
      style={{
        color: 'white',
        backgroundColor: 'var(--color-accent-secondary)',
        position: 'relative'
      }}
    >
      {onToggleCollapse && (
        <UnstyledButton
          onClick={onToggleCollapse}
          visibleFrom="sm"
          style={{
            position: 'absolute',
            left: (collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED) - 12,
            top: 28,
            width: 24,
            height: 24,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--color-accent-secondary)',
            border: '1px solid rgba(229,235,241,0.18)',
            cursor: 'pointer',
            zIndex: 1
          }}
        >
          <Box
            style={{
              display: 'flex',
              transform: collapsed ? 'none' : 'rotate(180deg)',
              transition: 'transform 150ms ease'
            }}
          >
            <IconFigmaChevronRight size={14} color={SIDEBAR_TEXT} />
          </Box>
        </UnstyledButton>
      )}

      <Group
        pl={collapsed ? 0 : 16}
        h={76}
        gap={10}
        wrap="nowrap"
        align="center"
        justify={collapsed ? 'center' : undefined}
        visibleFrom="sm"
        onClick={() => goTo(isAdmin ? '/admin' : '/')}
        style={{ cursor: 'pointer' }}
      >
        {collapsed ? (
          <Center w={36} h={36} style={{ borderRadius: 8, backgroundColor: MINI_LOGO_BG }}>
            <IconFigmaBrandMark size={16} color={SIDEBAR_TEXT} />
          </Center>
        ) : (
          <Stack gap={1}>
            <Text
              fz={22}
              c="white"
              lh={1.3}
              style={{ fontFamily: "'Iceland', sans-serif", fontWeight: 400 }}
            >
              {t('brand.name')}
            </Text>
            <Text fz={8.4} c="rgba(229,235,241,0.6)" lh={1.3} style={{ letterSpacing: 0.8 }}>
              {t('brand.tagline')}
            </Text>
          </Stack>
        )}
      </Group>

      <Divider color="rgba(229,235,241,0.12)" visibleFrom="sm" />

      {collapsed ? (
        <Stack gap={2} px={16} py={12} visibleFrom="sm">
          {renderNavItems(true)}
        </Stack>
      ) : (
        <Stack gap={2} px={16} py={12} visibleFrom="sm">
          {renderNavItems(false)}
        </Stack>
      )}

      <Stack gap={2} px={16} py={12} hiddenFrom="sm">
        {renderNavItems(false)}
      </Stack>

      <Box style={{ flex: 1 }} />

      <Divider color="rgba(229,235,241,0.12)" />

      <Box visibleFrom="sm">{renderFooter(collapsed)}</Box>
      <Box hiddenFrom="sm">{renderFooter(false)}</Box>
    </Stack>
  );
};
